import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Download, ArrowRight } from 'lucide-react';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { payment } = location.state || {};

  if (!payment) {
    navigate('/');
    return null;
  }

  const activatedDate = new Date(payment.verified_at);
  const expiryDate = new Date(activatedDate);
  expiryDate.setDate(expiryDate.getDate() + (payment.service_duration || 30));

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center py-8 px-4">
      <Card className="max-w-2xl w-full shadow-2xl">
        <CardContent className="p-8">
          {/* Success Icon */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">✅ PAYMENT VERIFIED!</h1>
            <p className="text-lg text-gray-600">
              Great news! Your payment has been verified and your service is now active.
            </p>
          </div>

          <div className="border-t border-b border-gray-200 py-6 my-6">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600">Order ID</p>
                <p className="text-lg font-bold text-gray-900">{payment.order_id}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Service</p>
                <p className="text-lg font-bold text-gray-900">{payment.service_type?.replace('_', ' ')}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Activated</p>
                <p className="font-semibold text-green-600">{activatedDate.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Expires</p>
                <p className="font-semibold text-orange-600">{expiryDate.toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">VERIFIED PAYMENT DETAILS:</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-bold text-gray-900">Rs. {payment.amount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Bank:</span>
                <span className="font-semibold text-gray-900">{payment.selected_bank}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Verified By:</span>
                <span className="font-semibold text-gray-900">Admin Team</span>
              </div>
              {payment.transfer_reference && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Reference:</span>
                  <span className="font-semibold text-gray-900">{payment.transfer_reference}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/post-ad')}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-6 text-lg"
            >
              Start Using Service
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="w-full py-6"
              onClick={() => window.print()}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Invoice
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/dashboard')}
            >
              View My Orders
            </Button>
          </div>

          {/* Thank You Message */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Thank you for using <span className="font-bold text-blue-600">Zeilian</span>! 🚀
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;